import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import {
  ColumnDetail,
  ExportCustomGridInfo,
  ExportTemplate,
  Range,
  SelectionPropertiesItem,
  SidePanelTab,
  SidePanelView
} from '@store/search-board/search-board.model';
import { select, Store } from '@ngrx/store';
import { selectCustomExportMaxNumberSelection, selectExportCustomGridInfo, selectIsPremiumViewAllowed, selectSearchResultsCount, selectSidePanelState } from '@store/search-board/search-board.selector';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { AppState } from '@root/app-state.module';
import { DataElement, PreferenceCode, PreferenceElement, PreferenceOption, Template, TemplateCode, TemplateType } from '@store/user/user.model';
import { Attribute, AttributesCategory } from '@shared/components/categories-attributes-selector/categories-attributes-selector.model';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { CreateCustomExport, DeleteCustomExport, UpdateCustomExport } from '@store/search-board/search-board.actions';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { UntypedFormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { selectPreferenceElement } from '@store/user/user.selector';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';
import { MixpanelService } from '@shared/services/mixpanel.service';

@UntilDestroy()
@Component({
  selector: 'rlst-customize-export',
  templateUrl: './customize-export.component.html',
  styleUrls: ['./customize-export.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomizeExportComponent implements OnInit {
  public currentExport: Template = null;
  public editNameControl: UntypedFormControl;

  public exportInfo$: Observable<ExportCustomGridInfo>;
  public exportFormsList: Array<Template> = [];

  public maxNumber$: Observable<number>;

  public categories: Array<AttributesCategory> = [];
  public selectedCategory: AttributesCategory = null;

  private allAttributes: Array<Attribute> = [];
  public attributesToDisplay: Array<Attribute> = [];
  public selectedItems: Array<SelectionPropertiesItem> = [];

  public isMobileNextStep: boolean = false;
  public isAdmin: boolean = false;
  public selectionHasChanges: boolean = false;

  public range: Range = null;
  public isExportRangeValid: boolean = true;
  public checkedPropertiesCount: number;
  private availableLimit: number = 0;
  private maxNumberRecordsToSearch: number;

  private readonly dataElementsList: Array<DataElement> = [];

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);

  constructor(private readonly store$: Store<AppState>,
              private readonly dialogRef: MatDialogRef<CustomizeExportComponent>,
              private readonly confirmationService: ConfirmationService,
              private readonly cdr: ChangeDetectorRef,
              @Inject(MAT_DIALOG_DATA) public data: { checkedPropertiesCount: number, shouldUseCheckedPropertiesCount: boolean },
              private readonly mixpanelService: MixpanelService) {
  }

  ngOnInit(): void {
    this.createForm();
    this.exportInfo$ = combineLatest(
      [
        this.store$.pipe(select(selectExportCustomGridInfo)),
        this.store$.pipe(select(selectPreferenceElement(
            PreferenceCode.PG_EXPORT_CONFIG,
            PreferenceCode.PREF_EXPORT_LAST_LOADED_TEMPLATE
          )
        )),
        this.store$.pipe(select(selectPreferenceElement(
          PreferenceCode.PG_COMMON_PREFERENCE,
          PreferenceCode.PREF_MAX_RECORDS_TO_SEARCH
          )
        )),
        this.store$.pipe(select(selectSearchResultsCount)),
        this.store$.pipe(select(selectIsPremiumViewAllowed)),
      ])
      .pipe(
        filter(([info, _]: [ExportCustomGridInfo, PreferenceElement, PreferenceElement, number, boolean]) => Object.keys(info).length !== 0),
        tap(([info, lastLoadedPrefElement, maxRecordsElement, searchResultsCount, isPremiumViewAllowed]: [ExportCustomGridInfo, PreferenceElement, PreferenceElement, number, boolean]) => {
          this.selectedItems = [];
          this.checkedPropertiesCount = this.data.shouldUseCheckedPropertiesCount ? this.data.checkedPropertiesCount : this.getCheckedPropertiesCount(maxRecordsElement, searchResultsCount);
          this.availableLimit = info.availableLimit;
          this.categories = info.exportColumnCategories.map((categoryName: string) => {
            return {name: categoryName};
          });
          this.allAttributes = info.exportColumns.map((att: ColumnDetail) => {
            this.dataElementsList.push(att.dataElement);
            return {
              label: att.name,
              name: att.columnDataField,
              selected: false,
              categoryName: att.category,
              disabled: att.category === 'Premium Search' && !isPremiumViewAllowed
            };
          });
          this.setCategory(null);

          const exportsList = (info.exportFormsList || []).map((exportTemplate: ExportTemplate) => exportTemplate.template);
          this.exportFormsList = this.sortExportList(exportsList);
          if (this.exportFormsList.length) {
            const lastLoadedExportTemplate = this.exportFormsList.find((template: Template) =>
              template.templateCode === PreferenceUtils.getPreferenceValue(lastLoadedPrefElement));
            this.openCustomExport(lastLoadedExportTemplate || this.exportFormsList[0]);
          }
          this.selectionHasChanges = false;
        }),
        map(([info, _]: [ExportCustomGridInfo, PreferenceElement, PreferenceElement, number, boolean]) => info)
      );
    this.maxNumber$ = this.store$.pipe(
      select(selectCustomExportMaxNumberSelection)
    );

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
        this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);
        this.mixpanelService.track('Export - Customize Export Button', {
          currentPageName: this.searchMixpanelUtil.currentPageName,
          searchType: this.sidePanelTabOpened,
        });
      });
  }

  getCheckedPropertiesCount(
    maxRecordsElement: PreferenceElement,
    searchResultsCount: number
  ) {
    let maxNumber = 0;
    maxRecordsElement.prefOptions.forEach((option: PreferenceOption) => {
      if (+option.optionValue > maxNumber) {
        maxNumber = +option.optionValue;
      }
    });
    this.maxNumberRecordsToSearch = maxNumber;
    if (maxNumber > searchResultsCount) {
      return searchResultsCount;
    }
    return maxNumber;
  }

  public createNewExport(): void {
    if (this.selectionHasChanges) {
      this.confirmationService.openConfirmationModal().pipe(
        filter(Boolean)
      ).subscribe(() => {
        this.createNewCustomExport();
        this.cdr.markForCheck();
      });
    } else {
      this.createNewCustomExport();
    }
  }

  public saveExport(): void {
    const dataElements: Array<DataElement> = this.selectedItems.map((item: SelectionPropertiesItem) => {
      return this.dataElementsList.find((element: DataElement) => element.fieldCode === item.id);
    });

    if (this.currentExport) {
      const modifiedExport: Template = {
        ...this.currentExport,
        label: this.editNameControl.value,
        dataElementList: dataElements,
        modifiedDate: new Date().toJSON()
      };
      this.store$.dispatch(new UpdateCustomExport(modifiedExport));
    } else {
      const newExport: Template = {
        templateCode: TemplateCode.TMPL_EMPTY,
        label: this.editNameControl.value,
        type: TemplateType.EXPORT,
        layoutType: 0,
        nbrOfColumns: 0,
        displayWhenEmpty: false,
        customTemplate: true,
        systemTemplateCustomized: false,
        dataElementList: dataElements,
        modifiedDate: new Date().toJSON(),
        childTemplateList: [{} as Template],
        hasAccess: true,
        inherited: false
      };
      this.store$.dispatch(new CreateCustomExport(newExport));
    }
  }

  public openExport(exportForDisplay: Template): void {
    if (this.selectionHasChanges) {
      this.confirmationService.openConfirmationModal().pipe(
        filter(Boolean)
      ).subscribe(() => {
        this.openCustomExport(exportForDisplay);
        this.cdr.markForCheck();
      });
    } else {
      this.openCustomExport(exportForDisplay);
    }
  }

  public deleteExport(): void {
    this.confirmationService.openConfirmationModal({
      title: 'Delete Confirmation',
      messageTitle: '',
      message: 'Please confirm you want to delete this ' + this.currentExport.label,
      confirmButtonLabel: 'Delete'
    }).pipe(
      filter(Boolean)
    ).subscribe(() => this.store$.dispatch(new DeleteCustomExport(this.currentExport.templateCode)));
  }

  public setCategory(category: AttributesCategory | null): void {
    this.selectedCategory = category;
    if (this.selectedCategory) {
      this.attributesToDisplay = this.allAttributes.filter((att: Attribute) => att.categoryName === this.selectedCategory.name);
    } else {
      this.attributesToDisplay = this.allAttributes;
    }
  }

  public selectAttribute(attribute: Attribute, isChanged?: boolean): void {
    if (attribute.selected) {
      this.selectedItems = [...this.selectedItems, this.mapToSelectedItems(attribute)];
    } else {
      this.selectedItems = this.selectedItems.filter((att: SelectionPropertiesItem) => att.id !== attribute.name);
    }
    if (isChanged) {
      this.selectionHasChanges = true;
      this.editNameControl.markAsTouched();
    }
  }

  public removeAttribute(att: SelectionPropertiesItem): void {
    const attribute = this.allAttributes.find((item: Attribute) => item.name === att.id);
    attribute.selected = false;
    this.selectAttribute({name: att.id, selected: false});
    this.attributesToDisplay = [...this.attributesToDisplay];
    this.selectionHasChanges = true;
  }

  public clearSelection(): void {
    this.allAttributes.forEach((att: Attribute) => att.selected = false);
    this.selectedItems = [];
    this.attributesToDisplay = [...this.attributesToDisplay];
    this.selectionHasChanges = true;
  }

  public toggleMobileNextStep(): void {
    this.isMobileNextStep = !this.isMobileNextStep;
  }

  public close(): void {
    if (this.selectionHasChanges) {
      this.confirmationService.openConfirmationModal({
        message: 'Do you want to close export without saving changes?',
        confirmButtonLabel: 'Close Export'
      }).pipe(
        filter(Boolean)
      ).subscribe(() => this.closeCustomExportModal());
    } else {
      this.closeCustomExportModal();
    }
  }

  public runExport(): void {
    const maxRecords = this.maxNumberRecordsToSearch || this.data.checkedPropertiesCount;
    let range = { from: 1, to: this.availableLimit < maxRecords ? this.availableLimit : maxRecords };

    if (!this.availableLimit) {
      return;
    }
    if (this.currentExport) {
      this.store$.dispatch(new SearchBoardActions.SetLastLoadedExportTemplate(this.currentExport));
    }
    const dataElements: Array<DataElement> = this.selectedItems.map((item: SelectionPropertiesItem) => {
      return {
        ...this.dataElementsList.find((element: DataElement) => element.fieldCode === item.id),
        childElements: []
      };
    });

    this.dialogRef.close({ range: this.range || range, selectedTab: this.sidePanelTabOpened, dataElements });

    const attributesSelected: Array<string> = new Array<string>();

    dataElements.map((dataItem: DataElement) => {
      attributesSelected.push(dataItem.label);
    });

    this.mixpanelService.track('Export - Customize Export Finalize', {
      currentPageName: this.searchMixpanelUtil.currentPageName,
      searchType: this.sidePanelTabOpened,
      recordsExported: this.checkedPropertiesCount,
      exportsRemaining: this.availableLimit - this.checkedPropertiesCount,
      attributesSelected: attributesSelected.join(', ')
    });

  }

  public registerChange(): void {
    this.selectionHasChanges = true;
  }

  public setRange(range: Range | null): void {
    this.range = range.from && range.to ? range : null;
  }

  public setExportRangeValidity(isValid: boolean): void {
    this.isExportRangeValid = isValid;
  }

  private mapToSelectedItems(selectedAtt: Attribute): SelectionPropertiesItem {
    return {fieldLabel: selectedAtt.label, id: selectedAtt.name, tooltip: selectedAtt.label};
  }

  private sortExportList(list: Array<Template>): Array<Template> {
    return list.sort((a: Template, b: Template) => {
      if (a.inherited !== b.inherited) {
        return a.inherited ? 1 : -1;
      } else {
        const toTimestamp = (st: string) => new Date(st).getTime();
        return toTimestamp(b.modifiedDate) - toTimestamp(a.modifiedDate);
      }
    });
  }

  private closeCustomExportModal(): void {
    this.mixpanelService.track('Export - Customize Export Cancel', {
      currentPageName: this.searchMixpanelUtil.currentPageName,
      searchType: this.sidePanelTabOpened,
    });
    this.dialogRef.close({dataElements: [], range: null});
    this.clearSelection();
    this.selectionHasChanges = false;
  }

  private createNewCustomExport(): void {
    this.currentExport = null;
    this.resetModalState('', false);
    this.clearSelection();
    this.selectionHasChanges = false;
  }

  private openCustomExport(exportForDisplay: Template): void {
    this.clearSelection();
    this.setCategory(null);
    this.currentExport = exportForDisplay;
    this.resetModalState(this.currentExport.label, this.currentExport.inherited);
    this.currentExport.dataElementList.forEach((el: DataElement) => {
      const defaultAtt = this.allAttributes.find((item: Attribute) => item.name === el.fieldCode);
      if (defaultAtt) {
        defaultAtt.selected = true;
        this.selectAttribute(defaultAtt);
      }
    });
    this.selectionHasChanges = false;
  }

  private createForm(): void {
    this.editNameControl =
      new UntypedFormControl({value: (this.currentExport || {label: 'New Export'}).label, disabled: this.isAdmin}, Validators.required);
    this.editNameControl.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (this.editNameControl.dirty) {
        this.selectionHasChanges = true;
      }
    });
  }

  private resetModalState(name: string, isAdmin: boolean): void {
    this.isAdmin = isAdmin;
    this.editNameControl.reset({value: name, disabled: this.isAdmin});
  }

  public isLimitExceeded(): boolean {
    return !this.availableLimit ||
      (this.range && (this.availableLimit < (this.range.to - this.range.from + 1)));
  }
}